import React, { useEffect, useState, useRef, useMemo } from 'react'
import { Table, Spin, Avatar, Button, Tag, Radio, Space, DatePicker } from 'antd'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'
import { PieChart, Pie, Cell, Legend } from 'recharts'

import Layout from '../../components/layout/Layout'
import axiosInstance from '../../utils/axiosInstance'

const { RangePicker } = DatePicker

const COLORS = ['green', '#FF8042']

function FeedVisits() {
  const navigate = useNavigate()

  const [logs, setLogs] = useState([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const isFetchingRef = useRef(false)
  const [filter, setFilter] = useState('all')
  const [dateRange, setDateRange] = useState(null)
  const [hideLUsers, setHideLUsers] = useState(true)
  const [cacheData, setCacheData] = useState([])

  const fetchLogs = async (pageNumber = 1) => {
    if (isFetchingRef.current) return

    setLoading(true)
    isFetchingRef.current = true

    try {
      const response = await axiosInstance.get(`/api/v1/logs/getScreenVisits?screenName=feed&page=${pageNumber}`)
      let newLogs = response.data.logs || []

      setLogs((prev) => [...prev, ...newLogs])
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
      isFetchingRef.current = false
    }
  }

  const columns = [
    {
      title: 'User',
      key: 'user',
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {/* {record.userDetails?.profilePicture && <Avatar src={record.userDetails.profilePicture} style={{ marginRight: 12 }} />} */}
          <div>
            <div>
              {record.userDetails?.firstName} {record.userDetails?.lastName}
            </div>
            <small>{record.userDetails?.email}</small>
          </div>
        </div>
      ),
    },
    {
      title: 'Cache Used',
      dataIndex: 'cacheUsed',
      key: 'cacheUsed',
      render: (v) => (v ? <Tag color="green">Yes</Tag> : <Tag color="red">No</Tag>),
    },
    {
      title: 'Page',
      dataIndex: 'page',
      key: 'page',
      render: (v) => {
        return v ? <p>{v}</p> : <p>-</p>
      },
    },
    {
      title: 'Club or Feed type',
      key: 'feedClubDetails',
      render: (record) => {
        if (record.myFeed == 'true') {
          return <Tag color="blue">Visiting own feed</Tag>
        } else if (record.feedClubDetails) {
          return <Tag color="magenta">{record.feedClubDetails.name}</Tag>
        } else if (record.feedUserDetails) {
          return (
            <>
              <Tag color="black">
                {record.feedUserDetails.firstName} {record.feedUserDetails.lastName}
              </Tag>
              <small>{record.feedUserDetails?.email}</small>
            </>
          )
        } else {
          return ''
        }
      },
    },
    {
      title: 'Execution time',
      key: 'approximateExecutionTime',
      render: (record) => {
        if (record.approximateExecutionTime) {
          const endOne = record.approximateExecutionTime[0]
          const endTwo = record.approximateExecutionTime[1] / 1e6 // convert nanoseconds to milliseconds
          return (
            <small>
              <span style={{ color: 'red' }}>{endOne > 0 ? `${endOne}s, ` : ``}</span>
              {endTwo.toFixed(2)}ms ({record.currentOnline})
            </small>
          )
        } else {
          return <p>-</p>
        }
      },
    },

    {
      title: 'Visited At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (v) => <small>{moment(v).format('h:mm:ss A, DD MMM YYYY')}</small>,
    },
  ]

  const filteredLogs = useMemo(() => {
    let result = [...logs]

    // Cache filter
    if (filter === 'cached') result = result.filter((log) => log.cacheUsed === true)
    if (filter === 'nonCached') result = result.filter((log) => log.cacheUsed === false)

    // Date range filter
    if (dateRange && dateRange.length === 2) {
      const [start, end] = dateRange
      result = result.filter((log) => log.createdAt >= start && log.createdAt <= end)
    }

    // Email "L" filter toggle
    if (hideLUsers) {
      result = result.filter((log) => log.userDetails?.email !== 'L')
    }

    // Cache stats derived from filtered result below
    let cachedHit = 0
    let cacheMiss = 0

    for (const log of result) {
      if (log.cacheUsed === true) cachedHit++
      else if (log.cacheUsed === false) cacheMiss++
    }

    setCacheData([
      { name: 'Cached Hit', value: cachedHit },
      { name: 'Cache Miss', value: cacheMiss },
    ])

    return result
  }, [logs, filter, dateRange, hideLUsers])

  useEffect(() => {
    fetchLogs(page)
  }, [page])

  return (
    <Layout>
      <div style={{ padding: 20 }}>
        <h2>Feed Visits</h2>

        <Space>
          <Button type="primary" onClick={() => navigate(-1)} style={{ marginBottom: 0 }}>
            Back
          </Button>

          <Radio.Group value={filter} onChange={(e) => setFilter(e.target.value)}>
            <Radio.Button value="all">All</Radio.Button>
            <Radio.Button value="cached">Cached Only</Radio.Button>
            <Radio.Button value="nonCached">Non Cached Only</Radio.Button>
          </Radio.Group>

          <RangePicker
            showTime
            onChange={(dates) => {
              if (!dates) {
                setDateRange(null)
              } else {
                setDateRange([dates[0].valueOf(), dates[1].valueOf()])
              }
            }}
          />

          {dateRange && <Button onClick={() => setDateRange(null)}>Clear Date</Button>}
        </Space>

        {filter == 'all' && (
          <PieChart width={200} height={200}>
            <Pie data={cacheData} cx="50%" cy="50%" outerRadius={80} fill="#8884d8" dataKey="value">
              {cacheData.map((entry, index) => (
                <Cell key={index} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Legend
              formatter={(value, entry) => {
                return `${value}: ${entry.payload.value}`
              }}
            />
          </PieChart>
        )}
        <p>Total items at table: {filteredLogs.length}</p>

        <Table rowKey="_id" columns={columns} dataSource={filteredLogs} pagination={false} scroll={{ x: 600, y: 500 }} />

        <div style={{ textAlign: 'center', marginTop: 16 }}>
          {loading ? (
            <Spin />
          ) : (
            <Button type="primary" onClick={() => setPage((prev) => prev + 1)}>
              Load More
            </Button>
          )}
        </div>
      </div>
    </Layout>
  )
}

export default FeedVisits
